import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/24/outline";

interface CarouselProps {
  children: React.ReactNode;
  /** Tailwind width classes for each slide */
  itemClassName?: string;
  gapClassName?: string;
  ariaLabelPrev?: string;
  ariaLabelNext?: string;
}

/**
 * Scroll-snap carousel: ← arrow, dots, → arrow (matches the Figma design).
 * Pure UI, no data logic.
 */
const Carousel: React.FC<CarouselProps> = ({
  children,
  itemClassName = "w-[85%] md:w-[48%] lg:w-[36%]",
  gapClassName = "gap-2",
  ariaLabelPrev = "Previous",
  ariaLabelNext = "Next",
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [pages, setPages] = useState(1);
  const [active, setActive] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);

  const update = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const pageCount =
      el.clientWidth > 0
        ? Math.max(1, Math.ceil(el.scrollWidth / el.clientWidth - 0.05))
        : 1;
    setPages(Math.min(pageCount, 6));
    setAtStart(el.scrollLeft <= 2);
    setAtEnd(el.scrollLeft >= maxScroll - 2);
    if (maxScroll <= 0) {
      setActive(0);
    } else {
      const ratio = el.scrollLeft / maxScroll;
      setActive(Math.round(ratio * (Math.min(pageCount, 6) - 1)));
    }
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    // Use ResizeObserver instead of window resize to avoid loop notifications
    let rafId: number;
    const ro = new ResizeObserver(() => {
      // Defer the update to next animation frame to avoid ResizeObserver loop
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(update);
    });
    ro.observe(el);

    el.addEventListener("scroll", update, { passive: true });

    // Initial measurement
    rafId = requestAnimationFrame(update);

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
      el.removeEventListener("scroll", update);
    };
  }, [update, children]);

  const scrollByPage = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.9, behavior: "smooth" });
  };

  const arrowBtn =
    "h-6 w-6 rounded-full bg-gray-100 text-gray-700 inline-flex items-center justify-center hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors";

  return (
    <div>
      <div
        ref={trackRef}
        className={`flex ${gapClassName} overflow-x-auto snap-x snap-mandatory scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`}
      >
        {React.Children.map(children, (child) => (
          <div className={`${itemClassName} shrink-0 snap-start min-w-0`}>
            {child}
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center justify-between">
        <button
          type="button"
          className={arrowBtn}
          onClick={() => scrollByPage(-1)}
          disabled={atStart}
          aria-label={ariaLabelPrev}
        >
          <ArrowLeftIcon className="h-3.5 w-3.5" />
        </button>

        <div className="flex items-center gap-1">
          {Array.from({ length: pages }).map((_, i) => (
            <span
              key={i}
              className={`h-1.5 w-1.5 rounded-full transition-colors ${
                i === active ? "bg-gray-900" : "bg-gray-300"
              }`}
            />
          ))}
        </div>

        <button
          type="button"
          className={arrowBtn}
          onClick={() => scrollByPage(1)}
          disabled={atEnd}
          aria-label={ariaLabelNext}
        >
          <ArrowRightIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

export default Carousel;
